import type {
  AriaAttributes,
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import type { AvatarSizes } from '@frameio/vapor';
import { theme, useHoverState, whenFocused } from '@frameio/vapor';
import { IconAccordionExpand } from '@frameio/vapor-icons';
import { ProjectHeaderContent } from './ProjectHeaderContent';

export type ProjectHeaderMenuTriggerProps = {
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
  avatarSize?: AvatarSizes;
  avatarUrl: string;
  className?: string;
  'data-show-blur'?: boolean;
  details: string;
  isForcedVisible?: boolean;
  isPrivate?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: () => void;
  projectId: string;
  projectName: string;
  style?: CSSProperties;
  tabIndex?: number;
};

export const ProjectHeaderMenuTrigger = forwardRef<
  HTMLDivElement,
  ProjectHeaderMenuTriggerProps
>(function ProjectHeaderMenuTrigger(
  {
    'aria-controls': ariaControls,
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHaspopup,
    avatarSize,
    avatarUrl,
    className,
    'data-show-blur': dataShowBlur,
    details,
    isForcedVisible = false,
    isPrivate,
    onKeyDown,
    onMouseDown,
    onMouseLeave,
    projectId,
    projectName,
    style,
    tabIndex,
  },
  ref
) {
  const { bind, isHovering } = useHoverState();

  return (
    <Container
      ref={ref}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      className={className}
      data-show-blur={dataShowBlur}
      data-testid="project-header-menu-trigger"
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onMouseEnter={bind.onMouseEnter}
      onMouseLeave={() => {
        bind.onMouseLeave();
        onMouseLeave?.();
      }}
      role="button"
      style={style}
      tabIndex={tabIndex}
    >
      <ProjectHeaderContent
        avatarSize={avatarSize}
        avatarUrl={avatarUrl}
        details={details}
        emphasis="prominent"
        isPrivate={isPrivate}
        projectId={projectId}
        projectName={projectName}
      />

      <ExpandIconContainer data-is-hovered={isHovering || isForcedVisible}>
        <IconAccordionExpand
          color={theme.colors.icon.primary}
          size={NAV_HEADER_ICON_SIZE}
        />
      </ExpandIconContainer>
    </Container>
  );
});

const NAV_HEADER_ICON_SIZE = 24;

const Container = styled.div`
  align-items: start;
  cursor: pointer;
  display: flex;
  height: auto;
  outline: none;
  padding: ${theme.spaces[12]} ${theme.spaces[16]} ${theme.spaces[12]}
    ${theme.spaces[12]};
  position: relative;
  width: 100%;

  ${whenFocused} {
    border-radius: ${theme.radii[8]};
    box-shadow: ${theme.shadows.interactive.focus};
  }
`;

const ExpandIconContainer = styled.div`
  align-self: center;
  border-radius: ${theme.spaces[6]};
  height: ${theme.spaces[NAV_HEADER_ICON_SIZE]};
  transition: background-color ${theme.transitions.durations[200]}
    ${theme.transitions.eases.primary};
  width: ${theme.spaces[NAV_HEADER_ICON_SIZE]};

  &[data-is-hovered='true'] {
    background-color: ${theme.colors.components.button.tertiary.interactive
      .background.hover};
  }
`;
